import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import logoHeader from "../../../assets/fluxmed-logo-horizontal.png";
import { ContentBoxTitle } from "../../../components/common";
import {
  FormButtonGroup,
  FormStack,
  FormTextInput,
} from "../../../components/form";
import { oids } from "../../../configs/Settings";
import { useSessionContext } from "../../../contexts/SessionContext";
import { Validate } from "../../../services/fhir";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useFormatter } from "../../../utils/useFormatter";
import { handleKeyPress } from "../../../utils/useKeyPress";
import { useRegex } from "../../../utils/useRegex";
import { IFormInput } from "../types";
import { ReviewSubscription } from "./ReviewSubscription";

interface IProps {
  getValue: any;
  control: any;
  handleSubmit: Function;
  navigateToNextPage: Function;
  setValue: any;
  onBackToSelection: () => void;
}

function onlyDigits(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

function isFhirOrganization(value: unknown): value is fhir5.Organization {
  if (!value || typeof value !== "object") return false;
  const v = value as { resourceType?: unknown; identifier?: unknown; name?: unknown };
  return (
    v.resourceType === "Organization" &&
    Array.isArray(v.identifier) &&
    typeof v.name === "string"
  );
}

type AltSocio = { cpf?: unknown; nome?: unknown };

function findMatchedSocioName(
  socios: unknown,
  userCpfDigits: string
): string | null {
  if (!userCpfDigits) return null;

  // Alguns retornos trazem `socios` como objeto, outros como lista.
  if (Array.isArray(socios)) {
    const match = (socios as AltSocio[]).find(
      (s) => onlyDigits(s?.cpf) === userCpfDigits
    );
    return match?.nome ? String(match.nome) : null;
  }

  if (socios && typeof socios === "object") {
    const s = socios as AltSocio;
    if (onlyDigits(s?.cpf) !== userCpfDigits) return null;
    return s?.nome ? String(s.nome) : null;
  }

  return null;
}

function normalizeAltToFhirOrganization(params: {
  alt: unknown;
  matchedSocioName: string;
  creatorCpf: string;
}): fhir5.Organization {
  const { alt, matchedSocioName, creatorCpf } = params;

  // Mantemos o mapeamento alinhado com o formato "esperado" já consumido no registro:
  // - `identifier[0].system = urn:oid:${oids.cnpj}` (para o `TermsOfUse` criar o id)
  // - `qualification[0].code.coding[]` contendo natureza jurídica e CNAEs
  // - `extension` com `OrganizationManager` (creator + nome do sócio)
  const a = alt as {
    ni?: unknown;
    nomeEmpresarial?: unknown;
    nomeFantasia?: unknown;
    endereco?: {
      uf?: unknown;
      municipio?: { descricao?: unknown } | unknown;
      pais?: { descricao?: unknown } | unknown;
      logradouro?: unknown;
      numero?: unknown;
      complemento?: unknown;
      bairro?: unknown;
      cep?: unknown;
    } | null;
    telefones?: { ddd?: unknown; numero?: unknown } | unknown;
    correioEletronico?: unknown;
    naturezaJuridica?: { codigo?: unknown; descricao?: unknown } | unknown;
    cnaePrincipal?: { codigo?: unknown; descricao?: unknown } | unknown;
    cnaeSecundarias?: Array<{ codigo?: unknown; descricao?: unknown }> | unknown;
  };

  const cnpjDigits = onlyDigits(a?.ni);
  const cepDigits = onlyDigits(a?.endereco?.cep);
  const state = a?.endereco?.uf ? String(a.endereco.uf) : "";
  const city =
    a?.endereco?.municipio &&
    typeof a.endereco.municipio === "object" &&
    (a.endereco.municipio as { descricao?: unknown })?.descricao
      ? String((a.endereco.municipio as { descricao?: unknown }).descricao)
      : "";
  const country =
    a?.endereco?.pais &&
    typeof a.endereco.pais === "object" &&
    (a.endereco.pais as { descricao?: unknown })?.descricao
      ? String((a.endereco.pais as { descricao?: unknown }).descricao)
      : "BRASIL";

  const addressLine: string[] = [
    a?.endereco?.bairro ? String(a.endereco.bairro) : "",
    a?.endereco?.logradouro ? String(a.endereco.logradouro) : "",
    a?.endereco?.numero ? String(a.endereco.numero) : "",
    a?.endereco?.complemento ? String(a.endereco.complemento) : "",
  ].filter(Boolean);

  const phoneDigits =
    a?.telefones && typeof a.telefones === "object"
      ? `${onlyDigits((a.telefones as { ddd?: unknown }).ddd)}${onlyDigits(
          (a.telefones as { numero?: unknown }).numero
        )}`
      : "";

  const naturezaJuridicaCoding = (() => {
    const nj =
      a?.naturezaJuridica && typeof a.naturezaJuridica === "object"
        ? (a.naturezaJuridica as { codigo?: unknown; descricao?: unknown })
        : undefined;
    if (!nj?.codigo && !nj?.descricao) return null;
    return {
      system: "http://example.org/naturezaJuridica",
      code: nj?.codigo ? String(nj.codigo) : "",
      display: nj?.descricao ? String(nj.descricao) : "",
    };
  })();

  const cnaePrincipalCoding = (() => {
    const cp =
      a?.cnaePrincipal && typeof a.cnaePrincipal === "object"
        ? (a.cnaePrincipal as { codigo?: unknown; descricao?: unknown })
        : undefined;
    if (!cp?.codigo && !cp?.descricao) return null;
    return {
      system: cp?.codigo ? String(cp.codigo) : "",
      display: cp?.descricao ? String(cp.descricao) : "",
      use: "official",
    };
  })();

  const cnaeSecundariasCoding = Array.isArray(a?.cnaeSecundarias)
    ? (a.cnaeSecundarias as Array<{ codigo?: unknown; descricao?: unknown }>)
        .map((c) => ({
          system: "cnaeSecundaria",
          value: c?.codigo ? String(c.codigo) : "",
          display: c?.descricao ? String(c.descricao) : "",
        }))
        .filter((c) => c.value || c.display)
    : [];

  const qualificationCoding = [
    ...(naturezaJuridicaCoding ? [naturezaJuridicaCoding] : []),
    ...(cnaePrincipalCoding ? [cnaePrincipalCoding] : []),
    ...cnaeSecundariasCoding,
  ];

  const organization = {
    resourceType: "Organization",
    id: "",
    name: a?.nomeEmpresarial ? String(a.nomeEmpresarial) : "",
    identifier: [
      {
        system: `urn:oid:${oids.cnpj}`,
        value: cnpjDigits,
      },
    ],
    contact: [
      {
        // Mantemos compatibilidade com o formato já esperado no registro (array em `address`)
        address: [
          {
            country,
            state,
            city,
            line: addressLine,
            postalCode: cepDigits,
          },
        ],
      },
      {
        telecom: [
          ...(phoneDigits
            ? [{ system: "phone", value: phoneDigits }]
            : []),
          ...(a?.correioEletronico
            ? [{ system: "email", value: String(a.correioEletronico) }]
            : []),
        ],
      },
    ],
    type: [
      {
        coding: [
          {
            system: "http://terminology.hl7.org/CodeSystem/organization-type",
            code: "prov",
            display: "Healthcare Provider",
          },
        ],
      },
    ],
    alias: [
      a?.nomeFantasia
        ? String(a.nomeFantasia)
        : a?.nomeEmpresarial
          ? String(a.nomeEmpresarial)
          : "",
    ].filter(Boolean),
    qualification: [
      {
        code: {
          coding: qualificationCoding,
        },
      },
    ],
    active: true,
    extension: [
      {
        url: "https://fluxmed.cloud/StructureDefinition/OrganizationManager",
        extension: [
          {
            url: "id",
            valueIdentifier: {
              system: "https://fluxmed.cloud/creator-id-system",
              value: creatorCpf,
            },
          },
          {
            url: "name",
            valueString: matchedSocioName,
          },
          {
            url: "purpose",
            valueString: "socio",
          },
        ],
      },
    ],
  };

  // O backend deveria entregar FHIR válido; aqui normalizamos para o formato esperado no registro.
  return organization as unknown as fhir5.Organization;
}

export function FormSubscription({
  onBackToSelection,
  getValue,
  handleSubmit,
  control,
  navigateToNextPage,
  setValue,
}: IProps) {
  const { user } = useSessionContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<number>(0);

  const onSubmit = async (data: IFormInput) => {
    setLoading(true);
    try {
      const validation = await Validate({
        cpf: user.username,
        cnpj: `${data.cnpjValue.replace(/[^a-zA-Z0-9]/g, "")}`,
      });
      if (validation?.status === HTTPResponseStatus.Conflict) {
        toast.warn(validation?.data);
        navigate("/");
        return;
      }
      if (validation?.status !== HTTPResponseStatus.Created) {
        toast.error(validation?.data);
        return;
      }

      const validationData: unknown = validation?.data;
      if (isFhirOrganization(validationData)) {
        setValue("organization", validationData);
      } else {
        // Fallback de segurança: quando o backend retorna payload alternativo,
        // validamos no frontend se o CPF logado consta como sócio.
        const userCpfDigits = onlyDigits(user.username);
        const alt = validationData as { socios?: unknown } | null | undefined;
        const matchedSocioName = findMatchedSocioName(
          alt?.socios,
          userCpfDigits
        );
        if (!matchedSocioName) {
          toast.error(
            "O CPF do usuário não coincide com nenhum sócio do CNPJ inserido"
          );
          return;
        }
        const organizationFhir = normalizeAltToFhirOrganization({
          alt: validationData,
          matchedSocioName,
          creatorCpf: user.username,
        });
        setValue("organization", organizationFhir);
      }

      localStorage.setItem(
        "organizationValue",
        `${oids.cnpj}-${data.cnpjValue.replace(/[^a-zA-Z0-9]/g, "")}`
      );

      setStep((prev: number) => prev + 1);
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {step === 0 && (
        <form
          onKeyDown={(event) =>
            handleKeyPress(event, false, handleSubmit(onSubmit))
          }
        >
          <FormStack>
            <img
              src={logoHeader}
              alt="Logo fluxmed"
              width={185}
              height="auto"
            />
            <ContentBoxTitle>Registrar clínica</ContentBoxTitle>
            <Typography variant="body1" textAlign="center">
              Insira o CNPJ da sua organização de saúde
            </Typography>
            <Stack width="100%">
              <Stack
                sx={{
                  display: "grid",
                  gridTemplateColumns: "100% 20px",
                }}
                width="100%"
                gap={2}
                alignItems="center"
              >
                <FormTextInput
                  name="cnpjValue"
                  control={control}
                  label="CNPJ"
                  required
                  pattern={useRegex.cnpj}
                  mask={useFormatter.formatCNPJ}
                />
              </Stack>
              <Stack width="100%" mt={3}>
                <FormButtonGroup
                  onGoBackButtonClick={onBackToSelection}
                  onNextButtonClick={handleSubmit(onSubmit)}
                  goBackButtonText="Voltar"
                  nextButtonText="Próximo"
                  loading={loading}
                />
              </Stack>
            </Stack>
          </FormStack>
        </form>
      )}
      {step === 1 && (
        <ReviewSubscription
          organization={getValue("organization")}
          onBackButtonClick={() => setStep((prev: number) => prev - 1)}
          onNextButtonClick={navigateToNextPage}
        />
      )}
    </>
  );
}
